import React from 'react';
import { NavLink, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { getSubscriptionStatus, verifyPaymentSession } from '../../rtk/thunks/paymentsThunk';
import { trackPurchase } from '../../utils/analytics';
import './Payment.css';

const formatError = (value) => {
    if (!value) return 'Payment verification failed.';
    if (typeof value === 'string') return value;
    if (Array.isArray(value)) return value.filter(Boolean).join(' ');

    return value.detail || value.message || value.error || 'Payment verification failed.';
};

const MAX_STATUS_POLLS = 12;
const POLL_INTERVAL_MS = 5000;

const PaymentSuccess = () => {
    const dispatch = useDispatch();
    const verificationStartedRef = React.useRef(false);
    const [searchParams] = useSearchParams();
    // Stripe appends session_id to the success URL. The webhook is the source
    // of truth, so we still poll subscription status when it is absent.
    const sessionId = searchParams.get('session_id');
                    const { paymentVerification, subscriptionStatus, verifyError, statusError } = useSelector((state) => state.payment);

  React.useEffect(() => {
    if (verificationStartedRef.current) return;
    verificationStartedRef.current = true;

    let cancelled = false;

    const pollStatus = async () => {
        for (let attempt = 0; attempt < MAX_STATUS_POLLS && !cancelled; attempt += 1) {
            const result = await dispatch(getSubscriptionStatus());
            if (result?.payload?.is_active) return;
            if (attempt < MAX_STATUS_POLLS - 1) {
                await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));
            }
        }
    };

    const verifyAndRefreshStatus = async () => {
        if (sessionId) {
            try {
                await dispatch(verifyPaymentSession({ session_id: sessionId })).unwrap();
            } catch (error) {
                // Fall through to polling - the webhook may still activate the subscription.
            }
        }

        await pollStatus();
    };

    verifyAndRefreshStatus();

    return () => {
        cancelled = true;
    };
}, [dispatch, sessionId]);

    const status = subscriptionStatus || paymentVerification;
    const isActive = Boolean(status?.is_active);
    const planName = status?.plan?.name || status?.plan?.title || status?.plan || 'Subscription';

    React.useEffect(() => {
        if (!isActive) return;
        const plan = status?.plan;
        trackPurchase({
            sessionId,
            planId: plan?.id,
            planName: typeof plan === 'object' ? plan?.name : undefined,
            value: plan?.price,
        });
    }, [isActive, sessionId, status?.plan?.id, status?.plan?.price]);

    return (
        <div className="payment-page">
            <div className="payment-card">
                <div className={`payment-status-icon ${isActive ? 'active' : ''}`}>
                    {isActive ? 'OK' : '!'}
                </div>

                <p className="payment-eyebrow">Payment status</p>
                <h1>{isActive ? 'Subscription active' : 'Confirming your payment...'}</h1>

                {verifyError && sessionId && (
                    <p className="payment-message error">{formatError(verifyError)}</p>
                )}

                <div className="payment-details">
                    <div>
                        <span>Status</span>
                        <strong>{status?.status || 'pending'}</strong>
                    </div>
                    <div>
                        <span>Plan</span>
                        <strong>{isActive ? planName : 'No active plan yet'}</strong>
                    </div>
                    <div>
                        <span>Days remaining</span>
                        <strong>{status?.days_remaining ?? 0}</strong>
                    </div>
                    <div>
                        <span>End date</span>
                        <strong>{status?.end_date || 'N/A'}</strong>
                    </div>
                </div>

                {!isActive && (
                    <p className="payment-message muted">
                        This can take a moment while Stripe confirms your payment. Feel free to check your dashboard - it will unlock automatically once confirmed.
                    </p>
                )}

                {statusError && (
                    <p className="payment-message muted">
                        We could not refresh your subscription status right now. Check your dashboard shortly.
                    </p>
                )}

                <div className="payment-actions">
                    <NavLink to="/dashboard" className="payment-primary-link">Go to dashboard</NavLink>
                    <NavLink to="/" className="payment-secondary-link">Back to home</NavLink>
                </div>
            </div>
        </div>
    );
};

export default PaymentSuccess;
